<script setup lang="ts">
/**
 * Фича-окружения — макет 90:538: сводка карточками, таблица живых стендов,
 * подсказка про CI внизу.
 *
 * Стенды собираются из журнала операций: своей сущности у окружения в трекере
 * нет, есть «подняли» и «погасили». Из-за этого экран знает ровно то, что знает
 * журнал: репозиторий, ветку, задачу, кто нажал и когда. Хост окружения, расход
 * ядер и лимит в 8 стендов из макета показать неоткуда — это записано в
 * docs/design-gaps.md.
 */
import { AppButton, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, RefreshCw, Search, Server } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useAuthStore } from '@/features/auth/store'
import {
  ACTION_STATUS,
  dropStand,
  listActions,
  raiseStand,
  toStands,
  type DevOpsAction,
  type Stand,
} from '@/features/devops/api'
import DevOpsMetric from '@/features/devops/components/DevOpsMetric.vue'
import RaiseStandDialog from '@/features/devops/components/RaiseStandDialog.vue'
import StandsTable from '@/features/devops/components/StandsTable.vue'
import { isIdle } from '@/features/devops/format'

const { t } = useI18n()
const auth = useAuthStore()

const actions = ref<DevOpsAction[]>([])
const isLoading = ref(false)
const error = ref('')
const busyId = ref('')
const isRaiseOpen = ref(false)
const search = ref('')
const projectFilter = ref<string[]>([])
const stateFilter = ref<string[]>([])

const allowed = computed(() => auth.can('tracker:devops'))

const stands = computed(() => toStands(actions.value))

/** Проекты для фильтра — те, что встретились в журнале: других стендов нет. */
const projectOptions = computed(() =>
  [...new Set(stands.value.map((stand) => stand.projectPath))].map((path) => ({
    value: path,
    label: path,
  })),
)

const stateOptions = computed(() =>
  (['active', 'building', 'idle'] as const).map((value) => ({
    value,
    label: t(`devops.envs.state.${value}`),
  })),
)

const stateOf = (stand: Stand) => {
  if (stand.status === ACTION_STATUS.pending) return 'building'

  return isIdle(stand.raisedAt) ? 'idle' : 'active'
}

const visible = computed(() => {
  const needle = search.value.trim().toLowerCase()

  return stands.value.filter((stand) => {
    if (projectFilter.value.length && !projectFilter.value.includes(stand.projectPath)) return false
    if (stateFilter.value.length && !stateFilter.value.includes(stateOf(stand))) return false
    if (!needle) return true

    return [stand.projectPath, stand.branch, stand.issueKey, stand.issueTitle]
      .join(' ')
      .toLowerCase()
      .includes(needle)
  })
})

const activeCount = computed(() => stands.value.filter((s) => stateOf(s) === 'active').length)
const buildingCount = computed(() => stands.value.filter((s) => stateOf(s) === 'building').length)
const idleCount = computed(() => stands.value.filter((s) => stateOf(s) === 'idle').length)

/** Что именно сейчас поднимается — подпись карточки: одна строка, самая свежая. */
const buildingHint = computed(() => {
  const building = stands.value.find((stand) => stateOf(stand) === 'building')

  return building ? building.issueKey || building.branch : ''
})

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    actions.value = await listActions()
  } catch {
    error.value = t('devops.envs.error')
  } finally {
    isLoading.value = false
  }
}

const run = async (stand: Stand, action: 'redeploy' | 'drop') => {
  busyId.value = stand.id

  try {
    if (action === 'drop') await dropStand(stand.projectPath, stand.branch)
    else await raiseStand(stand.issueId, stand.projectPath, stand.branch)

    await load()
  } catch {
    error.value = t('devops.envs.actionError')
  } finally {
    busyId.value = ''
  }
}

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[{ label: t('nav.devops'), to: '/devops' }, { label: t('devops.envs.title') }]"
  >
    <FilterChip
      v-model="projectFilter"
      :label="t('devops.envs.filters.project')"
      :options="projectOptions"
      :empty-text="t('devops.envs.filters.noProjects')"
    />
    <FilterChip
      v-model="stateFilter"
      :label="t('devops.envs.filters.state')"
      :options="stateOptions"
      :empty-text="t('devops.envs.filters.noProjects')"
    />

    <button
      type="button"
      class="envs__refresh"
      :aria-label="t('devops.envs.refresh')"
      :title="t('devops.envs.refresh')"
      @click="load"
    >
      <RefreshCw :size="15" />
    </button>

    <div class="envs__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('devops.envs.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('devops.envs.searchPlaceholder')"
      />
    </div>

    <AppButton v-if="allowed" variant="primary" size="small" @click="isRaiseOpen = true">
      {{ t('devops.stand.raise') }}
    </AppButton>
  </TrackerToolbar>

  <div class="envs">
    <div class="envs__metrics">
      <DevOpsMetric
        :label="t('devops.envs.metrics.active')"
        :value="activeCount"
        :hint="t('devops.envs.metrics.activeHint')"
        tone="success"
      />
      <DevOpsMetric
        :label="t('devops.envs.metrics.building')"
        :value="buildingCount"
        :hint="buildingHint"
        tone="info"
      />
      <DevOpsMetric
        :label="t('devops.envs.metrics.idle', { days: 3 })"
        :value="idleCount"
        :hint="t('devops.envs.metrics.idleHint')"
        tone="warning"
      />
    </div>

    <div v-if="isLoading && !stands.length" class="envs__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('devops.envs.error')"
      :description="t('devops.envs.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!visible.length"
      :icon="Server"
      :title="t('devops.envs.empty.title')"
      :description="t('devops.envs.empty.description')"
    />

    <StandsTable
      v-else
      :stands="visible"
      :busy-id="busyId"
      @redeploy="run($event, 'redeploy')"
      @drop="run($event, 'drop')"
    />

    <p class="envs__note">
      <AlertTriangle class="envs__note-icon" :size="16" />
      <span>{{ t('devops.envs.note') }}</span>
    </p>
  </div>

  <RaiseStandDialog v-if="isRaiseOpen" @done="((isRaiseOpen = false), load())" @close="isRaiseOpen = false" />
</template>

<style scoped>
.envs {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 28px 48px;
}

.envs__metrics {
  display: flex;
  gap: 12px;
}

.envs__skeleton {
  height: 320px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

/* Подсказка про CI — макет 90:952: серая плашка во всю ширину контента. */
.envs__note {
  display: flex;
  gap: 12px;
  margin: 0;
  padding: 12px 16px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}

.envs__note-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.envs__search {
  width: 220px;
}

.envs__refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.envs__refresh:hover {
  color: var(--color-text-primary);
}
</style>
